import { Component, computed, inject, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as XLSX from "xlsx";
import scheduleDataJson from "../../../json/re_dsm_meter_data.json";
import {
  ScheduleUploadRecord,
  ScheduleUploadStoreService,
} from "../../services/schedule-upload-store.service";

type Generator = {
  generator_id: number;
  name: string;
  type: string;
  qca_id: number;
  pss_id?: number;
  installed_capacity_mw: number;
  status: string;
};

type Pss = {
  pss_id: number;
  name: string;
  code: string;
  state: string;
  district: string;
  voltage_kv: number;
  installed_capacity_mw: number;
};
type Qca = {
  qca_id: number;
  name: string;
  code: string;
  state: string;
  status: string;
};
type Unit = {
  unit_id: number;
  generator_id: number;
  unit_code: string;
  unit_name: string;
  rated_capacity_mw: number;
  available_capacity_mw: number;
  status: string;
};
type Contract = {
  contract_id: number;
  generator_unit_id: number;
  capacity_mw: number;
  status: string;
};
type UploadRecord = ScheduleUploadRecord;
type ScheduleBlock = {
  blockNo: number;
  time: string;
  scheduleMw: number | null;
  availableCapacityMw: number | null;
};
type ScheduleData = {
  generators: Generator[];
  pss: Pss[];
  qca: Qca[];
  generator_units: Unit[];
  contracts: Contract[];
};

@Component({
  selector: "app-schedule-upload",
  standalone: true,
  templateUrl: "./schedule-upload.component.html",
  styleUrls: ["./schedule-upload.component.scss"],
})
export class ScheduleUploadComponent {
  private readonly data = scheduleDataJson as ScheduleData;
  private readonly uploadStore = inject(ScheduleUploadStoreService);

  readonly generators = this.data.generators.filter(
    (generator) => generator.status === "Operational",
  );
  readonly qcas = this.data.qca;
  readonly selectionType = signal<"QCA" | "Generator">("Generator");
  readonly selectedGeneratorId = signal(this.generators[0]?.generator_id ?? 0);
  readonly selectedQcaId = signal(this.data.qca[0]?.qca_id ?? 0);
  readonly selectedUnitId = signal<number | null>(null);
  readonly scheduleType = signal("Day Ahead");
  readonly scheduleDate = signal(this.today());
  readonly selectedFile = signal<File | null>(null);
  readonly previewBlocks = signal<ScheduleBlock[]>([]);
  readonly isFilePreviewOpen = signal(false);
  readonly dragActive = signal(false);
  readonly message = signal<{ text: string; error: boolean } | null>(null);
  readonly uploads = this.uploadStore.records;
  private readonly route = inject(ActivatedRoute);

  readonly selectedGenerator = computed(
    () =>
      this.generators.find(
        (generator) => generator.generator_id === this.selectedGeneratorId(),
      ) ?? null,
  );
  readonly units = computed(() =>
    this.data.generator_units.filter(
      (unit) => unit.generator_id === this.selectedGeneratorId(),
    ),
  );
  readonly selectedUnit = computed(
    () =>
      this.units().find((unit) => unit.unit_id === this.selectedUnitId()) ??
      null,
  );
  readonly pss = computed(
    () =>
      this.data.pss.find(
        (item) => item.pss_id === this.selectedGenerator()?.pss_id,
      ) ?? null,
  );
  readonly qca = computed(
    () =>
      this.data.qca.find(
        (item) => item.qca_id === this.selectedGenerator()?.qca_id,
      ) ?? null,
  );
  readonly selectedQca = computed(
    () =>
      this.data.qca.find((item) => item.qca_id === this.selectedQcaId()) ??
      null,
  );
  readonly displayedQca = computed(() =>
    this.selectionType() === "QCA" ? this.selectedQca() : this.qca(),
  );
  readonly contracts = computed(() =>
    this.data.contracts.filter(
      (contract) =>
        this.units().some(
          (unit) => unit.unit_id === contract.generator_unit_id,
        ) && contract.status === "Active",
    ),
  );
  readonly availableCapacity = computed(() =>
    this.units().reduce((total, unit) => total + unit.available_capacity_mw, 0),
  );
  readonly contractedCapacity = computed(() =>
    this.contracts().reduce(
      (total, contract) => total + contract.capacity_mw,
      0,
    ),
  );
  readonly nextRevisionNo = computed(() => {
    const revisionNumbers = this.uploads()
      .filter(
        (record) =>
          record.generatorId === this.selectedGeneratorId() &&
          record.unitId === this.selectedUnitId() &&
          record.scheduleType === this.scheduleType() &&
          record.scheduleDate === this.scheduleDate(),
      )
      .map((record) => Number(record.revisionNo.replace("Revision-", "")))
      .filter(Number.isFinite);

    return `Revision-${revisionNumbers.length ? Math.max(...revisionNumbers) + 1 : 0}`;
  });

  constructor(private readonly router: Router) {
    this.route.queryParamMap.subscribe((params) => {
      const type = params.get("type");
      if (type === "QCA" || type === "Generator") {
        this.changeSelectionType(type);
      }
      const qcaId = Number(params.get("qcaId"));
      if (type === "QCA" && this.qcas.some((qca) => qca.qca_id === qcaId)) {
        this.changeQca(String(qcaId));
      }
      const generatorId = Number(params.get("generatorId"));
      if (
        type !== "QCA" &&
        this.generators.some((generator) => generator.generator_id === generatorId)
      ) {
        this.changeGenerator(String(generatorId));
      }
    });
  }

  changeGenerator(value: string): void {
    this.selectedGeneratorId.set(Number(value));
    this.selectedUnitId.set(null);
  }

  changeSelectionType(value: string): void {
    this.selectionType.set(value === "QCA" ? "QCA" : "Generator");
    this.selectedUnitId.set(null);
  }

  changeQca(value: string): void {
    const qcaId = Number(value);
    this.selectedQcaId.set(qcaId);
    const generator = this.generators.find(
      (item) => item.qca_id === qcaId,
    );
    if (generator) this.selectedGeneratorId.set(generator.generator_id);
    this.selectedUnitId.set(null);
  }

  changeUnit(value: string): void {
    this.selectedUnitId.set(value ? Number(value) : null);
  }

  selectFile(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) void this.setFile(file);
  }

  dropFile(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) void this.setFile(file);
  }

  clearFile(): void {
    this.selectedFile.set(null);
    this.previewBlocks.set([]);
    this.isFilePreviewOpen.set(false);
    this.message.set(null);
    this.router.navigateByUrl('view-schedule');
  }

  openFilePreview(): void {
    if (this.selectedFile()) this.isFilePreviewOpen.set(true);
  }

  closeFilePreview(): void {
    this.isFilePreviewOpen.set(false);
  }

  async submit(): Promise<void> {
    try {
      const file = this.selectedFile();
      if (!file) {
        this.message.set({ text: "Choose an Excel schedule file before submitting.", error: true });
        return;
      }
      // const scheduleBlocks = await this.readScheduleBlocks(file);
      // if (!scheduleBlocks) {
      //   this.message.set({ text: "The Excel schedule data could not be read. Please select the template file again.", error: true });
      //   return;
      // }

      // const record: UploadRecord = {
      //   id: crypto.randomUUID(), generatorId: this.selectedGeneratorId(), unitId: this.selectedUnitId(),
      //   scheduleType: this.scheduleType(), revisionNo: this.nextRevisionNo(), scheduleDate: this.scheduleDate(),
      //   fileName: file.name, fileSize: file.size, uploadedAt: new Date().toISOString(), scheduleBlocks,
      // };
      // this.uploadStore.save(record);
      this.message.set({
        text: `${file.name} schedule data has been saved in this browser.`,
        error: false,
      });
      this.selectedFile.set(null);
      this.router.navigateByUrl("/view-schedule");
    } catch (error) {
      this.message.set({
        text: "Unable to save the schedule data in this browser.",
        error: true,
      });
    }
  }

  formatSize(bytes: number): string {
    return bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  downloadTemplate(): void {
    const headers = [
      "Block No",
      "Time",
      "Schedule (MW)",
      "Available Capacity (MW)",
    ];
    const rows: (string | number)[][] = [headers];
    for (let block = 1; block <= 96; block += 1)
      rows.push([block, this.blockTime(block), "", ""]);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = [{ wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 22 }];
    worksheet["!autofilter"] = { ref: "A1:D97" };
    worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };
    for (let row = 0; row <= 96; row += 1) {
      for (let column = 0; column < 4; column += 1) {
        const address = XLSX.utils.encode_cell({ r: row, c: column });
        const existing = worksheet[address] || { t: "z", v: "" };
        worksheet[address] = {
          ...existing,
          z: column === 0 ? "0" : column === 1 ? "@" : "0.00",
          s: {
            font:
              row === 0 ? { bold: true, color: { rgb: "FFFFFF" } } : undefined,
            fill: row === 0 ? { fgColor: { rgb: "1763E8" } } : undefined,
            alignment: {
              horizontal: row === 0 || column === 1 ? "center" : "left",
            },
            border: this.cellBorders(),
          },
        };
      }
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Block Schedule");
    XLSX.writeFile(workbook, "block_schedule_template.xlsx", {
      compression: true,
    });
  }

  private async setFile(file: File): Promise<void> {
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      this.message.set({ text: "Only .xlsx files are allowed.", error: true });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.message.set({
        text: "The file must be 10 MB or smaller.",
        error: true,
      });
      return;
    }
    const validationMessage = await this.validateScheduleFile(file);
    if (validationMessage) {
      this.message.set({ text: validationMessage, error: true });
      return;
    }
    this.selectedFile.set(file);
    this.previewBlocks.set((await this.readScheduleBlocks(file)) ?? []);
    this.isFilePreviewOpen.set(true);
    this.message.set(null);
  }

  private async validateScheduleFile(file: File): Promise<string | null> {
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), {
        type: "array",
        cellDates: false,
      });
      const worksheet = workbook.Sheets[workbook.SheetNames[0] || ""];
      if (!worksheet) return "The workbook must contain a schedule worksheet.";
      const values = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
        header: 1,
        defval: "",
      });
      const headers =
        values[0]?.slice(0, 4).map((value) => String(value).trim()) || [];
      const expected = [
        "Block No",
        "Time",
        "Schedule (MW)",
        "Available Capacity (MW)",
      ];
      if (
        headers.length !== 4 ||
        headers.some((header, index) => header !== expected[index])
      )
        return "Use the downloaded template: the required four columns do not match.";
      const rows = values
        .slice(1)
        .filter((row) => row.some((value) => value !== ""));
      if (rows.length !== 96)
        return "The schedule file must contain exactly 96 data blocks.";
      const blockNumbers = new Set<number>();
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const block = Number(row[0]);
        if (
          !Number.isInteger(block) ||
          block !== index + 1 ||
          blockNumbers.has(block)
        )
          return "Block numbers must be unique and run consecutively from 1 to 96.";
        blockNumbers.add(block);
        if (this.normalizedTime(row[1]) !== this.blockTime(block))
          return `Block ${block} must use time ${this.blockTime(block)}.`;
        if (!this.isBlankOrNumber(row[2]) || !this.isBlankOrNumber(row[3]))
          return `Block ${block} must use numeric values for Schedule and Available Capacity when values are provided.`;
      }
      return null;
    } catch {
      return "This file could not be read as a valid Excel workbook.";
    }
  }

  private async readScheduleBlocks(
    file: File,
  ): Promise<ScheduleBlock[] | null> {
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), {
        type: "array",
        cellDates: false,
      });
      const worksheet = workbook.Sheets[workbook.SheetNames[0] || ""];
      if (!worksheet) return null;
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
        worksheet,
        { defval: "" },
      );
      return rows.map((row) => ({
        blockNo: Number(row["Block No"]),
        time: this.normalizedTime(row["Time"]),
        scheduleMw:
          row["Schedule (MW)"] === "" ? null : Number(row["Schedule (MW)"]),
        availableCapacityMw:
          row["Available Capacity (MW)"] === ""
            ? null
            : Number(row["Available Capacity (MW)"]),
      }));
    } catch {
      return null;
    }
  }

  private blockTime(block: number): string {
    const startMinutes = (block - 1) * 15;
    const endMinutes = block * 15;
    return `${this.formatMinutes(startMinutes)} - ${this.formatMinutes(endMinutes)}`;
  }

  private normalizedTime(value: unknown): string {
    return String(value).trim().replace(/\s*-\s*/g, " - ");
  }

  private formatMinutes(totalMinutes: number): string {
    const minutesInDay = totalMinutes % (24 * 60);
    return `${String(Math.floor(minutesInDay / 60)).padStart(2, "0")}:${String(minutesInDay % 60).padStart(2, "0")}`;
  }

  private isBlankOrNumber(value: unknown): boolean {
    return (
      value === "" ||
      value === null ||
      value === undefined ||
      Number.isFinite(Number(value))
    );
  }

  private cellBorders(): {
    top: { style: "thin" };
    left: { style: "thin" };
    bottom: { style: "thin" };
    right: { style: "thin" };
  } {
    return {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }
}
